import type { NextRouter } from 'next/router';
import type { Dispatch, SetStateAction } from 'react';
import type { AddItemVisibility } from 'src/components/add-item/types';
import type { FragmentType } from 'src/gql';
import { useState } from 'react';
import { useRouter } from 'next/router';
import { Separator } from '@savvy-kit/components/separator';
import { Text } from '@savvy-kit/foundations/text';
import { AccountsTableSkeleton } from 'src/components/shared/accounts-table-skeleton';
import { UpdateType } from 'src/constants/portfolio';
import { graphql, unmaskFragment } from 'src/gql';
import { showMessageBeforeRefetch, updateToast } from 'src/utils/portfolio';

import type { PortfolioItemGroup } from '@savvy/constants/generated/gql-types';
import type { ID } from '@savvy/utils/id';
import { PORTFOLIO_ITEM_GROUP_SAVVY_ACCOUNTS } from '@savvy/constants/portfolio-item-groups';
import { USER_PROFILE_ADVISOR } from '@savvy/constants/user-profiles';
import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import { useHouseholdWriteAccess } from '../../hooks/use-household-write-access';
import AddItemDialog from '../add-item/views/add-item-dialog';
import ProviderAccountToast from '../portfolio/views/provider-account-toast';
import NetWorthAccountGroupTable from './net-worth-account-group-table';
import { PendingProviderAccountRow } from './pending-provider-account-row';
import ConnectAccountPreview from './views/connect-account-preview';

const ASSIGNED_ADVISOR_QUERY = graphql(/* GraphQL */ `
  query AssignedAdvisorFirstName($id: ID!, $householdId: ID) {
    client(id: $id) {
      assignedAdvisor(id: $householdId) {
        user {
          firstName
        }
      }
      household {
        uuid
        personalPortfolio {
          id
        }
      }
    }
  }
`);

export const NetWorthAccountsFragment = graphql(/* GraphQL */ `
  fragment NetWorthAccounts on Query {
    portfolio(userId: $id, portfolioId: $portfolioId, householdId: $householdId) {
      id
      portfolioItemGroups {
        ...NetWorthAccountGroupTable
        items {
          __typename
        }
        name
      }
    }
  }
`);

export type PortfolioDataToRender = {
  portfolio?: {
    portfolioItemGroups?: PortfolioItemGroup[];
  };
};

type NetWorthAccountsProps = {
  readonly clientUserId: string;
  readonly householdId?: string;
  readonly hiddenAccountsView: boolean;
  readonly queryFrag?: FragmentType<typeof NetWorthAccountsFragment>;
  readonly portfolioRefetch: () => Promise<unknown>;
  readonly showAddItemDialog: boolean;
  readonly setShowAddItemDialog: Dispatch<SetStateAction<AddItemVisibility | undefined>>;
  readonly emptyPortfolio?: boolean;
  readonly showConnectAccountsNudgeCard?: boolean;
};

export function handleItemUpdate({
  id,
  type,
  portfolioRefetch,
  router,
  householdUuid,
}: {
  id: ID;
  type: UpdateType;
  portfolioRefetch: () => Promise<unknown>;
  router: NextRouter;
  householdUuid?: string | null;
}) {
  if (type === UpdateType.CREATE_PROVIDER_ACCOUNT) {
    const destination = householdUuid
      ? `/advisor/dashboard/client/${householdUuid}/net-worth?newProviderId=${id}`
      : `/portfolio?newProviderId=${id}`;
    void router.replace(destination);
    return Promise.resolve();
  }
  if (showMessageBeforeRefetch(type)) {
    updateToast(type);
  }
  return portfolioRefetch()
    .then(() => {
      if (!showMessageBeforeRefetch(type)) {
        updateToast(type);
      }
    })
    .catch((err: unknown) => {
      console.error(err);
    });
}

function NetWorthAccounts({
  clientUserId,
  householdId,
  hiddenAccountsView,
  queryFrag,
  portfolioRefetch,
  showAddItemDialog,
  setShowAddItemDialog,
  emptyPortfolio,
  showConnectAccountsNudgeCard = false,
}: NetWorthAccountsProps) {
  const portfolioDataToRender = unmaskFragment(NetWorthAccountsFragment, queryFrag);
  const router = useRouter();
  const advisorView = !!clientUserId;
  const { user } = useAuth();
  const { canWrite } = useHouseholdWriteAccess();
  const { data: advisorData } = useQueryWithErrorLogging(ASSIGNED_ADVISOR_QUERY, {
    variables: {
      id: clientUserId || user?.id || '',
      householdId,
    },
  });

  const nonSavvyAccounts = (portfolioDataToRender?.portfolio.portfolioItemGroups.slice(1) || []).reduce(
    (acc: PortfolioItemGroup[], curr) => {
      // @ts-expect-error Existing accumulator flattens item arrays through the group type.
      return acc.concat(curr.items);
    },
    [],
  );

  const isSmallScreen = useIsSmallScreen();

  const householdUuid = advisorView ? (advisorData?.client.household.uuid ?? null) : null;

  const [pendingProviderAccount, setPendingProviderAccount] = useState<{ providerName: string | null } | null>(null);
  const hasPendingProviderAccount = !!router.query['newProviderId'] && !!pendingProviderAccount;

  return (
    <>
      {!portfolioDataToRender && <div className="mb-6" />}
      <div className="max-w-full">
        {!!router.query['newProviderId'] && (
          <ProviderAccountToast
            isReconnect={router.query['action'] === 'reconnect'}
            providerAccountId={String(router.query['newProviderId'])}
            clientUserId={clientUserId || undefined}
            householdId={householdId}
            onPendingProviderAccount={(providerName) => {
              setPendingProviderAccount({ providerName });
            }}
            onAccountsLoaded={() => {
              setPendingProviderAccount(null);
              void router.replace(window.location.pathname, undefined, {
                shallow: true,
              });
              void portfolioRefetch();
            }}
          />
        )}
        {hasPendingProviderAccount && <PendingProviderAccountRow providerName={pendingProviderAccount.providerName} />}
        {!portfolioDataToRender && <AccountsTableSkeleton title="Investments" />}
        {portfolioDataToRender?.portfolio.portfolioItemGroups
          .filter((portfolioItemGroup) =>
            hiddenAccountsView
              ? portfolioItemGroup.name !== PORTFOLIO_ITEM_GROUP_SAVVY_ACCOUNTS
              : portfolioItemGroup.items.length > 0,
          )
          .map((portfolioItemGroup) => (
            <div key={portfolioItemGroup.name}>
              <NetWorthAccountGroupTable
                personalPortfolioId={advisorData?.client.household.personalPortfolio?.id ?? null}
                portfolioItemGroupFrag={portfolioItemGroup}
                hiddenAccountsView={hiddenAccountsView}
                clientUserId={clientUserId}
                advisorView={advisorView}
                advisorName={advisorData?.client.assignedAdvisor.user.firstName}
                onEdit={({ id, type }: { id: ID; type: UpdateType }) => {
                  return handleItemUpdate({
                    id,
                    type,
                    portfolioRefetch,
                    router,
                    householdUuid,
                  });
                }}
              />
              {isSmallScreen && <Separator className="mt-4 bg-border-light" />}
            </div>
          ))}
      </div>

      {/* When true, ConnectAccountsNudgeCard already renders next to the graph in
          NetWorthPortfolio — suppress the old preview here so the two never double up. */}
      {!showConnectAccountsNudgeCard &&
        !isSmallScreen &&
        portfolioDataToRender &&
        (emptyPortfolio || nonSavvyAccounts.length === 0) &&
        user?.profileType !== USER_PROFILE_ADVISOR &&
        canWrite && (
          <>
            {!emptyPortfolio && nonSavvyAccounts.length === 0 && (
              <div className="flex max-w-full flex-row items-center gap-4 border-b border-b-border-light pb-2 pt-10">
                <Text variant="h4">External Accounts</Text>
                <Text color="secondary">$0</Text>
              </div>
            )}
            <ConnectAccountPreview setShowAddItemDialog={setShowAddItemDialog} />
          </>
        )}

      {showAddItemDialog && (
        <AddItemDialog
          hiddenFromClient={hiddenAccountsView}
          onSuccess={({ id, type }) => {
            setShowAddItemDialog(undefined);
            return handleItemUpdate({ id, type, portfolioRefetch, router, householdUuid });
          }}
          onClose={() => {
            setShowAddItemDialog(undefined);
          }}
        />
      )}
    </>
  );
}

export default NetWorthAccounts;
